import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export default function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/my/notifications").then(({ data: d }) => setData(d)).catch(() => {});

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/my/notifications/read-all").catch(() => {});
      setData((d) => ({ items: d.items.map((n) => ({ ...n, is_read: true })), unread: 0 }));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={toggle}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-slate-500 transition-colors duration-200 hover:border-brand hover:text-brand"
      >
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span
            data-testid="notifications-unread"
            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-black text-white"
          >
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-12 z-40 w-80 rounded-2xl border border-line bg-white p-2 shadow-xl"
        >
          <p className="px-3 py-2 text-xs font-extrabold text-ink">Notifications</p>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-slate-400">Aucune notification pour le moment.</p>
            ) : (
              data.items.map((n) => (
                <Link
                  key={n.id}
                  to={n.link || "/compte/commandes"}
                  onClick={() => setOpen(false)}
                  data-testid={`notification-item-${n.id}`}
                  className={`block rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-brand-soft ${
                    n.is_read ? "" : "bg-brand-soft/60"
                  }`}
                >
                  <p className="text-xs font-bold text-ink">{n.title}</p>
                  {n.message && <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">{n.message}</p>}
                  <p className="mt-0.5 text-[10px] text-slate-400">{formatDateTime(n.created_at)}</p>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
